/**
 * Pure helpers for the survey responses screen: period ranges, metric
 * normalization and turning the raw `answers` hash into rows a human can read.
 */
import { RESPONSE_STATUS_TONES } from '../constants';
import { isChoiceType } from './surveyFormHelper';

export const EMPTY_VALUE = '—';

/**
 * Epoch-second range consumed by the API (`DateRangeHelper` convention).
 * `days = 0` means "all time", so no range is sent at all.
 */
export const periodRange = (days, now = new Date()) => {
  const parsed = Number.parseInt(days, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return {};

  const until = Math.floor(now.getTime() / 1000);
  const since = until - parsed * 24 * 60 * 60;
  return { since: String(since), until: String(until) };
};

export const responseStatusTone = status =>
  RESPONSE_STATUS_TONES[String(status || '').toLowerCase()] || 'neutral';

export const canExportSurveyResponses = (user, accountId) => {
  const accounts = Array.isArray(user?.accounts) ? user.accounts : [];
  const selectedAccount = accounts.find(
    account => String(account?.id) === String(accountId)
  );
  const permissions = Array.isArray(selectedAccount?.permissions)
    ? selectedAccount.permissions
    : [];

  return (
    selectedAccount?.role === 'administrator' ||
    permissions.includes('administrator')
  );
};

export const formatDateTime = (value, locale = 'pt-BR') => {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatScore = value => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EMPTY_VALUE;
  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(1);
};

export const formatPercent = value => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EMPTY_VALUE;
  const percent = parsed <= 1 && parsed > 0 ? parsed * 100 : parsed;
  return `${Math.round(percent)}%`;
};

/**
 * Renders one answer using the question definition, so a choice value becomes
 * its label and a boolean becomes a yes/no word supplied by the caller.
 */
export const formatAnswer = (question, value, labels = {}) => {
  const yes = labels.yes || 'Sim';
  const no = labels.no || 'Não';

  if (value === null || value === undefined || value === '') return EMPTY_VALUE;

  const type = question?.type;

  if (type === 'boolean') {
    if (value === true || value === 'true' || value === 1) return yes;
    if (value === false || value === 'false' || value === 0) return no;
    return String(value);
  }

  if (isChoiceType(type)) {
    const options = Array.isArray(question?.options) ? question.options : [];
    const values = Array.isArray(value) ? value : [value];
    const rendered = values
      .map(item => {
        const option = options.find(
          candidate => String(candidate?.value) === String(item)
        );
        return option?.label || String(item);
      })
      .filter(Boolean);
    return rendered.length ? rendered.join(', ') : EMPTY_VALUE;
  }

  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};

/**
 * Aligns a response's `answers` hash with the form questions, keeping the
 * question order and surfacing answers whose question was later removed.
 */
export const buildAnswerRows = (questions, answers, labels = {}) => {
  const list = Array.isArray(questions) ? questions : [];
  const payload = answers && typeof answers === 'object' ? answers : {};
  const rows = list.map(question => ({
    key: question?.key,
    label: question?.label || question?.key || '',
    type: question?.type || 'text',
    value: formatAnswer(question, payload[question?.key], labels),
    answered:
      payload[question?.key] !== undefined &&
      payload[question?.key] !== null &&
      payload[question?.key] !== '',
  }));

  const knownKeys = new Set(list.map(question => question?.key));
  Object.keys(payload)
    .filter(key => !knownKeys.has(key))
    .forEach(key => {
      rows.push({
        key,
        label: key,
        type: 'text',
        value: formatAnswer({ type: 'text' }, payload[key], labels),
        answered: true,
        orphan: true,
      });
    });

  return rows;
};

export const normalizeOperationalProjection = projection => {
  if (
    !projection ||
    typeof projection !== 'object' ||
    Array.isArray(projection)
  ) {
    return null;
  }

  const stringValue = value => (typeof value === 'string' ? value : '');
  const targetId = projection.target_id;
  const targetType = stringValue(projection.target_type);
  const flow = stringValue(projection.flow);
  const isLeaseInspection = targetType === 'LeaseInspection';
  const isKycRequest = targetType === 'CrmTask' && flow === 'kyc_request';

  const normalized = {
    flow,
    status: stringValue(projection.status),
    target_type: targetType,
    target_id:
      typeof targetId === 'string' || typeof targetId === 'number'
        ? targetId
        : null,
    reason: stringValue(projection.reason),
    updated_at: stringValue(projection.updated_at) || null,
  };

  if (isLeaseInspection) {
    return {
      ...normalized,
      inspection_kind: stringValue(projection.inspection_kind) || null,
      inspection_status: stringValue(projection.inspection_status) || null,
      acceptance_status: stringValue(projection.acceptance_status) || null,
      inspected_at: stringValue(projection.inspected_at) || null,
      has_divergence: projection.has_divergence === true,
    };
  }

  if (isKycRequest) {
    return {
      ...normalized,
      kyc_document_status: stringValue(projection.kyc_document_status) || null,
      kyc_consent_accepted: projection.kyc_consent_accepted === true,
      identity_verification_status:
        stringValue(projection.identity_verification_status) || null,
    };
  }

  return normalized;
};

export const normalizeResponse = (response = {}) => {
  const raw = response && typeof response === 'object' ? response : {};
  return {
    id: raw.id ?? null,
    status: raw.status || 'pending',
    score: raw.score ?? null,
    answers: raw.answers && typeof raw.answers === 'object' ? raw.answers : {},
    contact: raw.contact || null,
    conversation_id: raw.conversation_id ?? null,
    subject_type: raw.subject_type || '',
    subject_id: raw.subject_id ?? null,
    delivery_channel: raw.delivery_channel || '',
    token_suffix: raw.token_suffix || '',
    sent_at: raw.sent_at || null,
    opened_at: raw.opened_at || null,
    responded_at: raw.responded_at || null,
    expires_at: raw.expires_at || null,
    created_at: raw.created_at || null,
    survey_form: raw.survey_form || null,
    operational_projection: normalizeOperationalProjection(
      raw.operational_projection
    ),
  };
};

export const contactLabel = (response, fallback = EMPTY_VALUE) => {
  const contact = response?.contact;
  if (!contact) return fallback;
  return (
    contact.name ||
    contact.available_name ||
    contact.email ||
    contact.phone_number ||
    fallback
  );
};

/**
 * Normalizes `GET survey_responses/metrics`. `score_distribution` and
 * `by_kind` arrive as objects keyed by score/kind; both are turned into sorted
 * arrays with a share percentage ready for the bars.
 */
export const normalizeMetrics = (metrics = {}) => {
  const raw = metrics && typeof metrics === 'object' ? metrics : {};
  const totalSent = Number(raw.total_sent) || 0;
  const totalSubmitted = Number(raw.total_submitted) || 0;

  const rawRate = Number(raw.response_rate);
  let responseRate = 0;
  if (Number.isFinite(rawRate) && rawRate > 0) {
    responseRate = Math.round(rawRate <= 1 ? rawRate * 100 : rawRate);
  } else if (totalSent > 0) {
    responseRate = Math.round((totalSubmitted / totalSent) * 100);
  }

  const distributionSource = raw.score_distribution || {};
  const distributionEntries = Array.isArray(distributionSource)
    ? distributionSource.map(item => [item?.score, item?.count])
    : Object.entries(distributionSource);

  const distributionTotal = distributionEntries.reduce(
    (total, [, count]) => total + (Number(count) || 0),
    0
  );

  const scoreDistribution = distributionEntries
    .map(([score, count]) => ({
      score: Number(score),
      count: Number(count) || 0,
      share: distributionTotal
        ? Math.round(((Number(count) || 0) / distributionTotal) * 100)
        : 0,
    }))
    .filter(item => Number.isFinite(item.score))
    .sort((left, right) => left.score - right.score);

  const kindSource = raw.by_kind || {};
  const kindEntries = Array.isArray(kindSource)
    ? kindSource.map(item => [item?.kind, item])
    : Object.entries(kindSource);

  // The API sends `by_kind` as `{ kind => { total_sent, total_submitted } }`,
  // but a plain `{ kind => count }` is accepted too.
  const kindCount = value => {
    if (value && typeof value === 'object') {
      return Number(value.total_submitted ?? value.count ?? 0) || 0;
    }
    return Number(value) || 0;
  };

  const byKind = kindEntries
    .map(([kind, value]) => ({ kind: String(kind), count: kindCount(value) }))
    .filter(item => item.kind)
    .sort((left, right) => right.count - left.count);

  const averageScore = Number(raw.average_score);

  return {
    totalSent,
    totalSubmitted,
    responseRate,
    averageScore: Number.isFinite(averageScore) ? averageScore : null,
    scoreDistribution,
    byKind,
  };
};

export const hasMetricsData = metrics =>
  Boolean(
    metrics &&
      (metrics.totalSent ||
        metrics.totalSubmitted ||
        metrics.scoreDistribution?.length)
  );
